import type { MemberCompletion } from '@/types/groups';
import type { QuizResult } from './persistence';
import { GroupContributionError } from './groups';

export type PreparedSubmission = {
  result: Omit<QuizResult, 'timestamp'>;
  completion: MemberCompletion;
};
type SubmissionStage = 'personal_result' | 'contribution';
export type GroupSubmissionCheckpoint = {
  phase: 'preparing' | 'saving_personal' | 'contributing' | 'failed' | 'complete';
  prepared?: PreparedSubmission;
  personalResultId?: string;
  failure?: { stage: SubmissionStage; status: number | null; reason: string | null };
};
export type GroupSubmissionState = {
  phase: 'saving' | 'contributing' | 'failed' | 'complete';
  stage: SubmissionStage;
  personalResultUrl?: string;
  status?: number | null;
  reason?: string | null;
  interrupted?: boolean;
};
type SubmissionEvent = 'group_quiz_save_attempted' | 'group_quiz_save_retried' | 'group_quiz_save_failed' | 'group_quiz_save_succeeded';

// One operation belongs to one completed quiz. It keeps the prepared score,
// answers, and successful result ID through failures; a retry cannot start a
// fresh quiz or create another personal result once a save was acknowledged.
export function createGroupSubmission(options: {
  groupId: string;
  quizType: string;
  prepare: () => Promise<PreparedSubmission>;
  savePersonalResult: (result: PreparedSubmission['result'], onAcknowledged?: (id: string) => void) => Promise<string>;
  initialCheckpoint?: GroupSubmissionCheckpoint;
  onCheckpoint?: (checkpoint: GroupSubmissionCheckpoint) => void;
  beforeAttempt?: () => Promise<void>;
  contribute: (groupId: string, completion: MemberCompletion) => Promise<void>;
  personalResultUrl: (id: string) => string;
  onState: (state: GroupSubmissionState) => void;
  onEvent: (event: SubmissionEvent, properties: { group_id: string; quiz_type: string; status: string | number }) => void;
  onSuccess: (personalResultUrl: string) => void;
}) {
  let prepared = options.initialCheckpoint?.prepared;
  let personalResultId = options.initialCheckpoint?.personalResultId;
  let personalResultUrl = personalResultId ? options.personalResultUrl(personalResultId) : undefined;
  let completed = false;
  let running: Promise<void> | null = null;
  const event = (name: SubmissionEvent, status: string | number) => options.onEvent(name, {
    group_id: options.groupId, quiz_type: options.quizType, status,
  });
  const checkpoint = (phase: GroupSubmissionCheckpoint['phase'], failure?: GroupSubmissionCheckpoint['failure']) => {
    options.onCheckpoint?.({ phase, prepared, personalResultId, failure });
  };
  const acknowledge = (id: string) => {
    if (!id) throw new Error('Personal result save returned no ID');
    personalResultId = id;
    prepared!.completion = { ...prepared!.completion, personalResultId: id };
    personalResultUrl = options.personalResultUrl(id);
    checkpoint('contributing');
  };

  async function attempt(retry: boolean) {
    let stage: SubmissionStage = personalResultUrl ? 'contribution' : 'personal_result';
    if (retry) event('group_quiz_save_retried', stage);
    event('group_quiz_save_attempted', stage);
    options.onState({ phase: personalResultUrl ? 'contributing' : 'saving', stage, personalResultUrl });
    try {
      await options.beforeAttempt?.();
      checkpoint(prepared ? (personalResultId ? 'contributing' : 'saving_personal') : 'preparing');
      prepared ??= await options.prepare();
      if (!personalResultUrl) {
        checkpoint('saving_personal');
        const id = await options.savePersonalResult(prepared.result, acknowledge);
        if (!personalResultId) acknowledge(id);
      }
      stage = 'contribution';
      options.onState({ phase: 'contributing', stage, personalResultUrl });
      await options.contribute(options.groupId, prepared.completion);
    } catch (error) {
      const status = error instanceof GroupContributionError ? error.status : null;
      const reason = error instanceof GroupContributionError ? error.reason : null;
      stage = personalResultId ? 'contribution' : 'personal_result';
      checkpoint('failed', { stage, status, reason });
      event('group_quiz_save_failed', status ?? `${stage}_failed`);
      options.onState({ phase: 'failed', stage, personalResultUrl, status, reason });
      return;
    }
    completed = true;
    checkpoint('complete');
    event('group_quiz_save_succeeded', 200);
    options.onState({ phase: 'complete', stage: 'contribution', personalResultUrl });
    options.onSuccess(personalResultUrl!);
  }

  return {
    run(retry = false): Promise<void> {
      if (running || completed) return running ?? Promise.resolve();
      running = attempt(retry).finally(() => { running = null; });
      return running;
    },
  };
}

export function getGroupSubmissionRecovery(state: GroupSubmissionState) {
  if (state.reason === 'identity_changed') return {
    title: 'Your browser session has changed',
    message: 'These answers belong to the browser session that started this quiz. Return to the group to continue with your current session.',
    retryable: false,
  };
  if (state.reason === 'question_changed') return {
    title: 'This group’s questions have changed',
    message: 'Return to the group to start with its current questions. Any personal result already saved is still available.',
    retryable: false,
  };
  if (state.stage === 'personal_result' && state.status && [403, 404, 409, 410].includes(state.status)) return {
    title: state.status === 410 ? 'This group has expired' : state.status === 409 ? 'This group has closed' : state.status === 403 ? 'You’re no longer a member of this group' : 'This group is no longer available',
    message: 'The group cannot accept these answers. Your progress is kept in this tab, but a personal result has not been saved yet.',
    retryable: false,
  };
  if (state.interrupted && state.stage === 'personal_result') return {
    title: 'Your result save was interrupted',
    message: 'Your answers are restored, but we did not receive a saved result ID. Trying again may create another result if the earlier save reached the server.',
    retryable: true,
  };
  if (state.stage === 'personal_result') return {
    title: 'We couldn’t save your result yet',
    message: 'Your answers are still here. Keep this page open and try saving again.',
    retryable: true,
  };
  if (state.reason === 'closed' || state.status === 409) return {
    title: 'This group has closed',
    message: 'The group no longer accepts answers. Your personal result is saved and ready to view.',
    retryable: false,
  };
  if (state.reason === 'expired' || state.status === 410) return {
    title: 'This group has expired',
    message: 'This comparison has expired and no longer accepts answers. Your personal result is saved.',
    retryable: false,
  };
  if (state.status === 404) return {
    title: 'This group is no longer available',
    message: 'The group is no longer available. Your personal result is saved and ready to view.',
    retryable: false,
  };
  if (state.reason === 'not_member' || state.status === 403) return {
    title: 'You’re no longer a member of this group',
    message: 'This browser is not a member of the group, so your answers could not be added. Your personal result is saved.',
    retryable: false,
  };
  if (state.status === 401) return {
    title: 'Your browser session could not be verified',
    message: 'We could not verify this browser session to add your answers to the group. Your personal result is saved.',
    retryable: false,
  };
  return {
    title: 'We couldn’t save your answers to the group',
    message: 'Your personal result is saved. Keep this page open and try again to add these answers to the group.',
    retryable: true,
  };
}
